/**
 * ConfigSchedulerPage
 *
 * Admin page to configure the background job scheduler for this org.
 * Reads/writes organisation/{org_id}/config/scheduler in Firestore via
 * the /admin/config/scheduler endpoints.
 *
 * Route: /settings/config/scheduler  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton, SecondaryButton, CompactButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import Loader from "../../components/Loader/Loader";
import { getServiceConfig, updateServiceConfig, deployScheduler } from "../../api/adminConfig";
import Checkbox from "../../components/Inputs/Checkbox";
import Toggle from "../../components/shared/Toggle";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { RefreshCw, Lock, Plus } from "lucide-react";
import { isDirty } from "../../utils/isDirty";

const ADAPTER_OPTIONS = [
  { value: "firestore", label: "Firestore (default)" },
];

const METHOD_OPTIONS = [
  { value: "POST", label: "POST" },
  { value: "GET",  label: "GET"  },
];

const DEFAULT_CLEANUP_JOBS = [
  {
    name:        "daily-cleanup",
    enabled:     true,
    schedule:    "0 2 * * *",
    http_method: "POST",
    path:        "/cleanup/scheduled",
    body:        '{"max_age_days": 30, "force": false}',
    description: "Daily file lifecycle cleanup",
  },
  {
    name:        "weekly-cleanup",
    enabled:     true,
    schedule:    "0 3 * * 0",
    http_method: "POST",
    path:        "/cleanup/scheduled",
    body:        '{"max_age_days": 7, "force": true}',
    description: "Weekly deep cleanup",
  },
  {
    name:        "cleanup-health",
    enabled:     true,
    schedule:    "0 * * * *",
    http_method: "GET",
    path:        "/cleanup/health",
    body:        "",
    description: "Hourly health check",
  },
];

const EMPTY_FORM = {
  adapter_type:          "firestore",
  gcp_project_id:        "",
  gcp_region:            "",
  enabled:               false,
  run_interval_minutes:  15,
  max_retries:           3,
  retry_delay_seconds:   60,
  webhook_secret:        "",
  cleanup_jobs:          DEFAULT_CLEANUP_JOBS,
};

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-gray-400 dark:text-ink-gray-500 pb-1 border-b border-edge-gray-200 dark:border-edge-gray-700">
    {children}
  </p>
);

const ConfigSchedulerPage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [deploying, setDeploying]     = useState(false);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);
  const [expandedJob, setExpandedJob] = useState(null);
  const [deployErrors, setDeployErrors] = useState(new Set());

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("scheduler");
      if (data.configured) {
        const loaded = {
          adapter_type:         data.adapter_type          ?? "firestore",
          gcp_project_id:       data.gcp_project_id        ?? "",
          gcp_region:           data.gcp_region            ?? "",
          enabled:              data.enabled               ?? false,
          run_interval_minutes: data.run_interval_minutes  ?? 15,
          max_retries:          data.max_retries           ?? 3,
          retry_delay_seconds:  data.retry_delay_seconds   ?? 60,
          webhook_secret:       data.webhook_secret        ?? "",
          cleanup_jobs:         data.cleanup_jobs?.length ? data.cleanup_jobs : DEFAULT_CLEANUP_JOBS,
        };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load scheduler config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const set = (field) => (e) => {
    setDeployErrors((prev) => { const next = new Set(prev); next.delete(field); return next; });
    setForm((prev) => ({ ...prev, [field]: e.target ? e.target.value : e }));
  };

  const setJobField = (index, field, value) =>
    setForm((prev) => ({
      ...prev,
      cleanup_jobs: prev.cleanup_jobs.map((j, i) => i === index ? { ...j, [field]: value } : j),
    }));

  const addJob = () => {
    setForm((prev) => ({
      ...prev,
      cleanup_jobs: [
        ...prev.cleanup_jobs,
        { name: "", enabled: true, schedule: "", http_method: "POST", path: "", body: "", description: "" },
      ],
    }));
    setExpandedJob(form.cleanup_jobs.length);
  };

  const removeJob = (index) =>
    setForm((prev) => ({ ...prev, cleanup_jobs: prev.cleanup_jobs.filter((_, i) => i !== index) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateServiceConfig("scheduler", {
        ...form,
        run_interval_minutes: Number(form.run_interval_minutes) || 15,
        max_retries:          Number(form.max_retries)          || 3,
        retry_delay_seconds:  Number(form.retry_delay_seconds)  || 60,
      });
      setOriginalForm(form);
      showFeedback("Scheduler config saved.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save scheduler config.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeploy = async () => {
    const missing = new Set();
    if (!form.gcp_project_id) missing.add("gcp_project_id");
    if (!form.gcp_region)     missing.add("gcp_region");
    if (!form.webhook_secret) missing.add("webhook_secret");
    if (missing.size > 0) {
      setDeployErrors(missing);
      showFeedback("GCP Project ID, Region, and Webhook Secret are required to deploy.", "error");
      return;
    }
    setDeployErrors(new Set());
    setDeploying(true);
    try {
      const result = await deployScheduler();
      const summary = Object.entries(result)
        .map(([k, v]) => `${k}: ${v}`)
        .join(" · ");
      showFeedback(`Deployed — ${summary}`, "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Deploy failed.", "error");
    } finally {
      setDeploying(false);
    }
  };

  return (
    <SettingsPageShell
      title="Scheduler Configuration"
      description="Configure the background job scheduler for prompt execution and file cleanup."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-8 max-w-lg">

          {/* ── GCP Infrastructure ── */}
          <div className="flex flex-col gap-4">
            <SectionLabel>GCP Infrastructure</SectionLabel>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                GCP Project ID
              </label>
              <div className={deployErrors.has("gcp_project_id") ? "ring-1 ring-focus-red-500 rounded-[10px]" : ""}>
                <Input
                  placeholder="Enter GCP project ID"
                  value={form.gcp_project_id}
                  onChange={set("gcp_project_id")}
                />
              </div>
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                GCP project where Cloud Scheduler jobs will be deployed.
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                GCP Region
              </label>
              <div className={deployErrors.has("gcp_region") ? "ring-1 ring-focus-red-500 rounded-[10px]" : ""}>
                <Input
                  placeholder="Enter GCP region"
                  value={form.gcp_region}
                  onChange={set("gcp_region")}
                />
              </div>
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                GCP region for Cloud Scheduler (e.g. <code className="font-mono">us-central1</code>). Should match your backend Cloud Run region.
              </p>
            </div>
          </div>

          {/* ── Prompt Scheduler ── */}
          <div className="flex flex-col gap-4">
            <SectionLabel>Prompt Scheduler</SectionLabel>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                Adapter
              </label>
              <Select
                value={form.adapter_type}
                onChange={(v) => setForm((prev) => ({ ...prev, adapter_type: v }))}
                options={ADAPTER_OPTIONS}
              />
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">Select the storage backend used to read scheduled prompts.</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                Run Interval (minutes)
              </label>
              <Input
                type="number"
                min={1}
                max={1440}
                value={form.run_interval_minutes}
                onChange={set("run_interval_minutes")}
              />
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">GCP fires the scheduler every minute, but prompt jobs are throttled to this interval. Set to 1 to run on every tick.</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                Max Retries
              </label>
              <Input
                type="number"
                min={0}
                max={20}
                value={form.max_retries}
                onChange={set("max_retries")}
              />
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">How many times a failed prompt job will be retried before being marked as failed. Set to 0 to disable retries.</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                Retry Delay (seconds)
              </label>
              <Input
                type="number"
                min={1}
                value={form.retry_delay_seconds}
                onChange={set("retry_delay_seconds")}
              />
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">Wait time between retry attempts for a failed prompt job.</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                Webhook Secret
              </label>
              <div className={`relative ${deployErrors.has("webhook_secret") ? "ring-1 ring-focus-red-500 rounded-[10px]" : ""}`}>
                <Input
                  placeholder="Required — set before deploying"
                  value={form.webhook_secret}
                  onChange={set("webhook_secret")}
                  className="pr-9"
                />
                <button
                  type="button"
                  title="Generate secret"
                  onClick={() => {
                    const bytes = new Uint8Array(32);
                    crypto.getRandomValues(bytes);
                    const secret = btoa(String.fromCharCode(...bytes))
                      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
                    setForm((prev) => ({ ...prev, webhook_secret: secret }));
                    setDeployErrors((prev) => { const next = new Set(prev); next.delete("webhook_secret"); return next; });
                  }}
                  className="absolute inset-y-0 right-2.5 flex items-center text-ink-copy-hover hover:text-ink-ink dark:hover:text-ink-white"
                  tabIndex={-1}
                >
                  <RefreshCw size={14} />
                </button>
              </div>
              <p className={`text-[11px] ${form.webhook_secret && form.webhook_secret.length < 32 ? "text-ink-amber-500 dark:text-ink-amber-400" : "text-ink-gray-400 dark:text-ink-gray-500"}`}>
                Use a random string of at least 32 characters — e.g. <code className="font-mono">secrets.token_urlsafe(32)</code>
              </p>
            </div>
          </div>

          {/* ── System Jobs ── */}
          <div className="flex flex-col gap-4">
            <SectionLabel>System Jobs</SectionLabel>
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500 -mt-2">Fixed infrastructure jobs provisioned automatically on every deploy. Cannot be disabled here — use the relevant settings page to control each job's behaviour.</p>

            {/* Prompt scheduler — read-only system job */}
            <div className="border border-edge-gray-200 dark:border-edge-gray-700 rounded-lg overflow-hidden opacity-80">
              <button
                type="button"
                onClick={() => setExpandedJob(expandedJob === "prompt" ? null : "prompt")}
                className="w-full flex items-center justify-between px-4 py-3 bg-fill-gray-50 dark:bg-fill-gray-800 text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-fill-green-600" />
                  <span className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">photonai-prompt-scheduler</span>
                  <span className="text-[11px] text-ink-gray-400 font-mono">* * * * *</span>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-fill-gray-200 dark:bg-fill-gray-700 text-ink-gray-500 dark:text-ink-gray-400 flex items-center gap-1">
                    <Lock size={9} />System
                  </span>
                </div>
                <span className="text-ink-gray-400 text-[10px]">{expandedJob === "prompt" ? "▲" : "▼"}</span>
              </button>

              {expandedJob === "prompt" && (
                <div className="flex flex-col gap-2 px-4 py-4 border-t border-edge-gray-200 dark:border-edge-gray-700">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                    <div><span className="text-ink-gray-400">Method</span><p className="font-mono text-ink-gray-700 dark:text-ink-gray-300 mt-0.5">POST</p></div>
                    <div><span className="text-ink-gray-400">Path</span><p className="font-mono text-ink-gray-700 dark:text-ink-gray-300 mt-0.5">/scheduler/run</p></div>
                    <div><span className="text-ink-gray-400">Schedule</span><p className="font-mono text-ink-gray-700 dark:text-ink-gray-300 mt-0.5">* * * * * (every minute)</p></div>
                    <div><span className="text-ink-gray-400">Body</span><p className="font-mono text-ink-gray-700 dark:text-ink-gray-300 mt-0.5">{'{}'}</p></div>
                  </div>
                  <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500 pt-1">This job is provisioned automatically. The effective run frequency is controlled by Run Interval above.</p>
                </div>
              )}
            </div>

            {/* Telemetry reporter — read-only system job */}
            <div className="border border-edge-gray-200 dark:border-edge-gray-700 rounded-lg overflow-hidden opacity-80">
              <button
                type="button"
                onClick={() => setExpandedJob(expandedJob === "telemetry" ? null : "telemetry")}
                className="w-full flex items-center justify-between px-4 py-3 bg-fill-gray-50 dark:bg-fill-gray-800 text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-fill-green-600" />
                  <span className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">photonai-telemetry-reporter</span>
                  <span className="text-[11px] text-ink-gray-400 font-mono">0 0 * * *</span>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-fill-gray-200 dark:bg-fill-gray-700 text-ink-gray-500 dark:text-ink-gray-400 flex items-center gap-1">
                    <Lock size={9} />System
                  </span>
                </div>
                <span className="text-ink-gray-400 text-[10px]">{expandedJob === "telemetry" ? "▲" : "▼"}</span>
              </button>

              {expandedJob === "telemetry" && (
                <div className="flex flex-col gap-2 px-4 py-4 border-t border-edge-gray-200 dark:border-edge-gray-700">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                    <div><span className="text-ink-gray-400">Method</span><p className="font-mono text-ink-gray-700 dark:text-ink-gray-300 mt-0.5">POST</p></div>
                    <div><span className="text-ink-gray-400">Path</span><p className="font-mono text-ink-gray-700 dark:text-ink-gray-300 mt-0.5">/telemetry/report</p></div>
                    <div><span className="text-ink-gray-400">Schedule</span><p className="font-mono text-ink-gray-700 dark:text-ink-gray-300 mt-0.5">0 0 * * * (daily midnight UTC)</p></div>
                    <div><span className="text-ink-gray-400">Body</span><p className="font-mono text-ink-gray-700 dark:text-ink-gray-300 mt-0.5">{'{}'}</p></div>
                  </div>
                  <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500 pt-1">Publishes a daily usage snapshot to QuantumZero for billing reconciliation. Provisioned automatically — toggle reporting on/off from the Telemetry settings page.</p>
                </div>
              )}
            </div>
          </div>

          {/* ── Background Jobs ── */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <SectionLabel>Background Jobs</SectionLabel>
              <CompactButton onClick={addJob}>
                <Plus className="w-3 h-3" />
                Add job
              </CompactButton>
            </div>
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500 -mt-2">Custom GCP Cloud Scheduler jobs for file lifecycle cleanup and other background tasks. Each job runs on its own cron schedule and calls a backend endpoint.</p>

            {form.cleanup_jobs.map((job, i) => (
              <div
                key={i}
                className="border border-edge-gray-200 dark:border-edge-gray-700 rounded-lg overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setExpandedJob(expandedJob === i ? null : i)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-fill-gray-50 dark:bg-fill-gray-800 text-left"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${job.enabled ? "bg-fill-green-600" : "bg-fill-gray-300"}`}
                    />
                    <span className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                      {job.name || <span className="text-ink-gray-400 italic">untitled</span>}
                    </span>
                    {job.schedule && (
                      <span className="text-[11px] text-ink-gray-400 font-mono">{job.schedule}</span>
                    )}
                  </div>
                  <span className="text-ink-gray-400 text-[10px]">{expandedJob === i ? "▲" : "▼"}</span>
                </button>

                {expandedJob === i && (
                  <div className="flex flex-col gap-3 px-4 py-4 border-t border-edge-gray-200 dark:border-edge-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Enabled</span>
                      <Checkbox
                        checked={job.enabled}
                        onChange={(e) => setJobField(i, "enabled", e.target.checked)}
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Name</label>
                      <Input
                        placeholder="e.g. daily-cleanup"
                        value={job.name}
                        onChange={(e) => setJobField(i, "name", e.target.value)}
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Schedule (cron)</label>
                      <Input
                        placeholder="e.g. 0 2 * * *"
                        value={job.schedule}
                        onChange={(e) => setJobField(i, "schedule", e.target.value)}
                      />
                    </div>

                    <div className="flex gap-3">
                      <div className="flex flex-col gap-1 w-28">
                        <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Method</label>
                        <Select
                          value={job.http_method}
                          onChange={(v) => setJobField(i, "http_method", v)}
                          options={METHOD_OPTIONS}
                        />
                      </div>
                      <div className="flex flex-col gap-1 flex-1">
                        <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Path</label>
                        <Input
                          placeholder="/cleanup/scheduled"
                          value={job.path}
                          onChange={(e) => setJobField(i, "path", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Body (JSON)</label>
                      <textarea
                        rows={2}
                        placeholder='{"max_age_days": 30}'
                        value={job.body}
                        onChange={(e) => setJobField(i, "body", e.target.value)}
                        className="photon-field w-full text-xs px-3 py-2 rounded-md border border-edge-gray-300 dark:border-edge-gray-700 bg-fill-white dark:bg-fill-gray-900 text-ink-gray-700 dark:text-ink-gray-300 font-mono resize-none focus:outline-none focus:ring-1 focus:ring-focus-hex-6366f1"
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Description</label>
                      <Input
                        placeholder="Brief description"
                        value={job.description}
                        onChange={(e) => setJobField(i, "description", e.target.value)}
                      />
                    </div>

                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={() => removeJob(i)}
                        className="text-xs text-ink-red-500 hover:text-ink-red-600 dark:text-ink-red-400 dark:hover:text-ink-red-500"
                      >
                        Remove job
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ── Enable toggle ── */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Enable Scheduler</p>
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">Job will not run until enabled and a webhook secret is set</p>
            </div>
            <Toggle
              checked={form.enabled}
              onChange={(v) => setForm((prev) => ({ ...prev, enabled: v }))}
            />
          </div>

          {/* ── Actions ── */}
          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || deploying || !isDirty(form, originalForm)}>
              {saving ? "Saving…" : "Save Config"}
            </PrimaryButton>
            {/* Deploy re-syncs the already-saved config to actual Cloud Scheduler jobs —
                an independent action, not gated on unsaved form changes. */}
            <SecondaryButton onClick={handleDeploy} disabled={deploying || saving}>
              {deploying ? "Deploying…" : "Deploy Scheduler"}
            </SecondaryButton>
          </div>

        </div>
      )}
    </SettingsPageShell>
  );
};

export default ConfigSchedulerPage;


